import React from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { AdminDashboardHeader } from '../AdminDashboard/AdminDashboardHeader';
import { useHowToList } from './useHowToList';
import { formatUpdatedDate } from './utils';

export const HowToListPage: React.FC = () => {
  const {
    authLoading,
    isAuthenticated,
    isAdmin,
    logout,
    guides,
    categories,
    isLoading,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    deletingId,
    error,
    handleDelete,
  } = useHowToList();

  if (authLoading || isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 spinner" />
          <p className="text-gray-600 font-medium">
            {authLoading ? 'Authenticating...' : 'Loading How To guides...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) return null;

  return (
    <div className="page-container">
      <AdminDashboardHeader onLogout={logout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-end mb-6">
          <Link to="/admin/howto/new" className="btn-primary flex items-center space-x-2">
            <Plus size={18} />
            <span>New How To guide</span>
          </Link>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-3 mb-6">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All categories</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Sort
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Updated
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {guides.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                    No How To guides yet. Create your first one.
                  </td>
                </tr>
              ) : (
                guides.map(guide => (
                  <tr key={guide.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{guide.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{guide.categoryName || '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          guide.status === 'published'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {guide.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{guide.sortOrder}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatUpdatedDate(guide.updatedAt)}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Link
                        to={`/admin/howto/${guide.id}/edit`}
                        className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                      >
                        <Pencil size={16} className="mr-1" />
                        Edit
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(guide.id)}
                        disabled={deletingId === guide.id}
                        className="inline-flex items-center text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        <Trash2 size={16} className="mr-1" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};
